import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';

interface PatientColumnHeaderProps {
  patientName: string;
  patientIdShort: string;
  isPrimary: boolean;
  selectedCount: number;
  totalCount: number;
  patientColor: string;
  patternStyle: { backgroundImage: string };
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onCollapse: () => void;
}

export function PatientColumnHeader({
  patientName,
  patientIdShort,
  isPrimary,
  selectedCount,
  totalCount,
  patientColor,
  patternStyle,
  onSelectAll,
  onDeselectAll,
  onCollapse,
}: PatientColumnHeaderProps) {
  const allSelected = selectedCount === totalCount && totalCount > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        px: 1.5,
        py: 1.5,
        borderBottom: 2,
        borderColor: patientColor,
        backgroundColor: `${patientColor}08`,
        ...patternStyle,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          backgroundColor: 'rgba(255,255,255,0.85)',
          borderRadius: 1,
          px: 1.25,
          py: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {patientName}
              </Typography>
              {isPrimary && (
                <Chip
                  label="Primary"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    backgroundColor: `${patientColor}18`,
                    color: patientColor,
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ fontFamily: 'monospace', fontSize: '0.6rem' }}>
              Patient {patientIdShort}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {selectedCount}/{totalCount} selected
            </Typography>
          </Box>

          <Tooltip title="Collapse column">
            <IconButton size="small" onClick={onCollapse} aria-label={`Collapse ${patientName} column`}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckBoxIcon sx={{ fontSize: 16 }} />}
            onClick={onSelectAll}
            disabled={allSelected}
            sx={{
              textTransform: 'none',
              fontSize: '0.7rem',
              py: 0.25,
              px: 1,
              minWidth: 0,
              flex: 1,
              backgroundColor: 'background.paper',
            }}
          >
            Select All
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 16 }} />}
            onClick={onDeselectAll}
            disabled={selectedCount === 0}
            sx={{
              textTransform: 'none',
              fontSize: '0.7rem',
              py: 0.25,
              px: 1,
              minWidth: 0,
              flex: 1,
              backgroundColor: 'background.paper',
            }}
          >
            Deselect All
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
